import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import EmptyState from "@/components/base/EmptyState";
import SectionHeading from "@/components/base/SectionHeading";
import { useSeo } from "@/hooks/useSeo";
import { useFavorites } from "@/hooks/useFavorites";
import { formatViews, getMovieById, getRelatedMovies } from "@/lib/catalog";

export default function DetailPage() {
  const { id } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  const related = useMemo(
    () => (movie ? getRelatedMovies(movie, 4) : []),
    [movie]
  );

  useSeo({
    title: movie
      ? `${movie.title} - 详细剧情介绍 - 年轻的母亲在线`
      : "影视详情 - 年轻的母亲在线",
    description: movie
      ? `${movie.title}（${movie.year}）${movie.genres.join("、")}影视，由${movie.director}执导，${movie.cast}主演。${movie.summary}`
      : "年轻的母亲在线影视详情页。",
    keywords: movie
      ? `${movie.title},${movie.genres.join(",")},年轻的母亲在线,剧情介绍,${movie.year}`
      : "年轻的母亲在线,影视详情",
  });

  if (!movie) {
    return (
      <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 py-16">
        <EmptyState
          title="抱歉，您查看的影视不存在或已下架。"
          description="该影视可能已被移除，或链接有误。你可以返回首页继续浏览其他精彩内容。"
          actionLabel="返回首页"
          actionTo="/"
        />
      </div>
    );
  }

  const favorited = isFavorite(movie.id);
  const categoryType = movie.type === "movie" ? "movie" : "tv";
  const categoryLabel = movie.type === "movie" ? "电影" : "电视剧";

  const handleFavorite = () => {
    const added = toggleFavorite(movie.id);
    setToast(added ? "已加入我的收藏" : "已取消收藏");
    window.setTimeout(() => setToast(""), 2000);
  };

  return (
    <div className="w-full max-w-[1440px] mx-auto px-4 md:px-6 py-8 md:py-10">
      <Breadcrumb
        items={[
          { label: "首页", to: "/" },
          { label: categoryLabel, to: `/category/${categoryType}` },
          { label: movie.title },
        ]}
      />

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-8">
        {/* 封面 */}
        <div className="w-full max-w-[280px] lg:max-w-none mx-auto lg:mx-0">
          <div className="w-full aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
            <img
              src={movie.cover}
              alt={`${movie.title} 海报`}
              title={`${movie.title} - 详细剧情介绍`}
              className="w-full h-full object-cover object-top"
            />
          </div>
          <button
            type="button"
            onClick={handleFavorite}
            className={`mt-4 w-full h-11 inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
              favorited
                ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                : "bg-background-100 border border-background-200 text-foreground-800 hover:border-primary-300 hover:text-primary-600"
            }`}
          >
            <i className={favorited ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
            {favorited ? "已收藏" : "收藏本片"}
          </button>
          {toast && (
            <p className="mt-2 text-center text-xs text-primary-600" role="status">
              {toast}
            </p>
          )}
        </div>

        {/* 信息 */}
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="px-2.5 h-7 rounded-md bg-secondary-100 text-secondary-800 text-xs flex items-center">
              {categoryLabel}
            </span>
            {movie.theme && (
              <span className="px-2.5 h-7 rounded-md bg-accent-100 text-accent-900 text-xs flex items-center">
                {movie.theme}
              </span>
            )}
          </div>

          <h1 className="mt-3 font-heading font-bold text-2xl md:text-4xl text-foreground-950 leading-tight">
            {movie.title}
          </h1>

          <div className="mt-3 flex items-center gap-4 flex-wrap text-sm">
            <span className="flex items-center gap-1 text-accent-700 font-semibold">
              <i className="ri-star-fill"></i>
              {movie.rating.toFixed(1)}
            </span>
            <span className="text-foreground-500">{movie.year} 年</span>
            <span className="text-foreground-500">{movie.region}</span>
            <span className="text-foreground-500">{movie.duration}</span>
            <span className="text-foreground-500 flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {formatViews(movie.views)}
            </span>
            <span className="text-foreground-500 flex items-center gap-1">
              <i className="ri-bookmark-line"></i>
              {formatViews(movie.favorites)}
            </span>
          </div>

          <p className="mt-5 text-sm md:text-[15px] text-foreground-700 leading-relaxed">
            {movie.summary}
          </p>

          <div className="mt-6 rounded-lg bg-background-100 border border-background-200 divide-y divide-background-200">
            <div className="flex gap-4 px-4 py-3">
              <span className="text-sm text-foreground-500 w-16 shrink-0">导演</span>
              <span className="text-sm text-foreground-900">{movie.director}</span>
            </div>
            <div className="flex gap-4 px-4 py-3">
              <span className="text-sm text-foreground-500 w-16 shrink-0">主演</span>
              <span className="text-sm text-foreground-900">{movie.cast}</span>
            </div>
            <div className="flex gap-4 px-4 py-3">
              <span className="text-sm text-foreground-500 w-16 shrink-0">类型</span>
              <span className="text-sm text-foreground-900">
                {movie.genres.join(" / ")}
              </span>
            </div>
            <div className="flex gap-4 px-4 py-3">
              <span className="text-sm text-foreground-500 w-16 shrink-0">所属分类</span>
              <span className="text-sm flex items-center gap-3">
                <Link
                  to={`/category/${categoryType}`}
                  className="text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  {categoryLabel}频道
                </Link>
                <Link
                  to="/category/topic"
                  className="text-primary-600 hover:text-primary-700 cursor-pointer"
                >
                  主题专题
                </Link>
              </span>
            </div>
          </div>

          <div className="mt-5 flex flex-wrap gap-2">
            {movie.tags.map((tag) => (
              <Link
                key={tag}
                to={`/search?q=${encodeURIComponent(tag)}`}
                className="px-3 py-1 text-xs rounded-full bg-background-100 border border-background-200 text-foreground-600 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
              >
                #{tag}
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* 剧情介绍 */}
      <section className="mt-12">
        <SectionHeading icon="ri-file-text-line" title="剧情介绍" />
        <article className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-7">
          {movie.synopsis.split("\n\n").map((para, i) => (
            <p
              key={i}
              className="text-sm md:text-[15px] text-foreground-700 leading-loose mb-4 last:mb-0"
            >
              {para}
            </p>
          ))}
        </article>
      </section>

      {/* 相关推荐 */}
      {related.length > 0 && (
        <section className="mt-12">
          <SectionHeading
            icon="ri-magic-line"
            title="相关推荐"
            subtitle="根据类型与主题标签为你精选的相似影视"
          />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-6">
            {related.map((item) => (
              <MovieCard key={item.id} movie={item} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}